import { ListButton } from "@refinedev/antd";
import {
  Alert,
  ConfigProvider,
  Select,
  Spin,
  Switch,
  Typography,
  message,
  Card,
  Collapse,
  Modal,
  InputNumber,
  Tooltip,
} from "antd";
import { useGetIdentity, useNavigation, useTranslate } from "@refinedev/core";
import { nivelDeOperadores } from "@/utilities/nivel-de-operadores";
import {
  CheckOutlined,
  MailOutlined,
  WhatsAppOutlined,
  CopyOutlined,
  ContactsOutlined,
  DeleteOutlined,
  ReadOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
  AppstoreOutlined,
  ScissorOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { Flex, Button, theme } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import {
  ChannelAccessPermissions,
  ChannelAccessPermissionsRef,
} from "@/components/channel-access-permissions";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import {
  axiosInstance,
  MARGIN_UNIT_OPTIONS,
  normalizeMarginUnit,
} from "@/utilities";
import {
  API_URL_CONTACTS,
  API_URL_PERMISSIONS,
  API_URL_PERMISSIONS_STORES,
  API_URL_USERS,
} from "@/services/XCONS_ENV";
import { useLocation, useParams } from "react-router-dom";
import { LateralModal } from "@/components/modals/LateralModal";
import { StoresUserPermissionsCopy } from "./userPermissionsCopy";
import {
  BoxCloseIcon,
  KeyRound,
  PresupuestoIcon,
  SellerIcon,
} from "@/components";
import { Robot2Line } from "@/components/icon/RobotIcon";

import { Trans } from "react-i18next";
import { opcionesDeCategorias } from "./categorias";
import { NIVEL, NIVEL_PARA_TODO } from "@/utilities/niveles-de-permiso";

const ADMIN_OPTIONS = [
  { label: NIVEL.admin, value: "admin" },
  { label: NIVEL.no_access, value: "no_access" },
];

const EDITOR_OPTIONS = [
  { label: NIVEL.editor, value: "editor" },
  { label: NIVEL.no_access, value: "no_access" },
];

const VIEWER_OPTIONS = [
  { label: NIVEL.viewer, value: "viewer" },
  { label: NIVEL.no_access, value: "no_access" },
];

const PARTICIPATION_OPTIONS = [
  { label: NIVEL.admin, value: "admin" },
  { label: NIVEL.editor, value: "editor" },
  { label: NIVEL.viewer, value: "viewer" },
  { label: NIVEL.no_access, value: "no_access" },
];

const PERMISSION_OPTIONS = [
  { label: NIVEL.admin, value: "admin" },
  { label: NIVEL.editor, value: "editor" },
  { label: NIVEL.viewer, value: "viewer" },
  { label: NIVEL.no_access, value: "no_access" },
];

const ALL_PERMISSION_OPTIONS = [
  { label: NIVEL_PARA_TODO.admin, value: "admin" },
  { label: NIVEL_PARA_TODO.editor, value: "editor" },
  { label: NIVEL_PARA_TODO.viewer, value: "viewer" },
  { label: NIVEL.by_relation, value: "by_relation" },
];

/** El permiso que marca al usuario como asistente IA del store (xcons-docs#561, D62–D63). */
const AGENT_CODE = "agent";

/** Lo que la API responde cuando el asistente tendría permisos fuera del store (D63). */
const AGENT_NOT_DEDICATED =
  "Un asistente IA no puede tener permisos en otro store, en sellers que no son de este store ni en el Catálogo. Sacale esos permisos, o no lo marques como asistente.";

/**
 * Un permiso es binario cuando el backend declara exactamente `[admin, no_access]`
 * (`switchOptions`). No se pregunta por el código: así cualquier permiso que nazca
 * binario se dibuja solo como SI/NO, sin tocar este panel.
 */
const esBinario = (item?: { options?: string[] }) =>
  Array.isArray(item?.options) &&
  item!.options!.length === 2 &&
  item!.options!.includes("admin") &&
  item!.options!.includes("no_access");

type PermissionItem = {
  code: string;
  value: string;
  description?: string;
  /** Las opciones que declara el contributor. Es lo que decide si la fila es SI/NO. */
  options?: string[];
};

type ConfigItem = {
  code: string;
  value: string;
  description?: string;
};

const { Option } = Select;
const { Title, Text } = Typography;
const { Panel } = Collapse;

export const StoresUserPermissionsPanel = ({
  typeIdentifier = "store",
}: {
  typeIdentifier?: string;
}) => {
  const [structureLoading, setStructureLoading] = useState<boolean>(false);
  // Grupo "Conversations" (N1): su estado vive en el conversation-service, así que persiste aparte.
  const channelPermsRef = useRef<ChannelAccessPermissionsRef>(null);
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [isUserActive, setIsUserActive] = useState<boolean>(true);
  const [storeName, setStoreName] = useState<string>("");
  const [groupOptions, setGroupOptions] = useState<any>({});
  const [userData, setUserData] = useState<any>();

  const t = useTranslate();
  const { token } = theme.useToken();
  const { goBack } = useNavigation();

  const location = useLocation();
  const { id } = useParams();
  const record = location.state?.record.user;

  const { smartMenuName } = useContext(SmartMenuContext);

  const storeIdentifier = encodeURIComponent(
    smartMenuName?.replace(/\"/g, "").toLowerCase(),
  );

  // Viewer en `store_operators` (xcons-docs#537): ve el detalle entero y no cambia nada. Todo lo
  // que escribe se deshabilita de una vez con `componentDisabled`, y los botones que disparan
  // una escritura no se dibujan. La API rechaza igual con 403; esto es para no ofrecer lo que no
  // va a andar.
  const { data: identity } = useGetIdentity<any>();
  const nivelOperadores = identity
    ? nivelDeOperadores(
        identity,
        smartMenuName?.replace(/\"/g, "").toLowerCase() ?? "",
      )
    : undefined;
  // Mientras la identidad no llegó, todo deshabilitado: un Lector no tiene que ver los
  // controles activos ni un instante, y a un Admin un deshabilitado que se habilita no le
  // cuesta nada. El aviso de arriba sí espera a saber que es Lector.
  const soloLectura = nivelOperadores !== "admin";
  const esLector = nivelOperadores === "viewer";

  const getUserData = async () => {
    try {
      const res = await axiosInstance.get(`${API_URL_USERS}/${id}`);
      const data = res.data;
      setUserData(data);
    } catch (error) {
      console.error(error);
      handleBack();
    }
  };

  const handleBack = () => {
    goBack();
  };

  const getPermissions = async () => {
    setStructureLoading(true);
    const userID = record?.userId || id;
    try {
      const res = await axiosInstance.get(
        `${API_URL_PERMISSIONS_STORES}/${storeIdentifier}/users/${userID}`,
      );

      const data = res.data;
      setIsUserActive(data.isActive);
      const permissionsData: PermissionItem[] = data.permissions || [];
      let configsData: ConfigItem[] = Array.isArray(data.configs)
        ? [...data.configs]
        : [];

      setPermissions(permissionsData);
      setConfigs(configsData);
      setStoreName(data.identifier);
    } catch (error) {
      console.error("Error al obtener permisos:", error);
    } finally {
      setStructureLoading(false);
    }
  };

  useEffect(() => {
    if (!record?.userId) {
      getUserData();
    }
    getPermissions();
  }, []);

  useEffect(() => {
    if (permissions) {
      const initialGroupOptions: Record<string, string> = {};

      PERMISSION_GROUPS.forEach((group) => {
        if (group.mainPermissionCode) {
          const found = permissions.find(
            (p) => p.code === group.mainPermissionCode,
          );

          if (found) {
            initialGroupOptions[group.key] = found.value;
          }
        }
      });

      setGroupOptions(initialGroupOptions);
    }
  }, [permissions]);

  const getPermission = (code: string) =>
    permissions.find((p) => p.code === code);

  const getConfig = (code: string) => configs.find((c) => c.code === code);

  const renderSelect = (item: any, isConfig = false, groupKey?: string) => {
    if (!item) return null;

    // Un permiso individual binario (Optimizer) es un switch, igual que los binarios de grupo.
    if (!groupKey && !isConfig && esBinario(item)) {
      const poner = (checked: boolean) =>
        setPermissions((prev: any) =>
          prev.map((p: any) =>
            p.code === item.code
              ? { ...p, value: checked ? "admin" : "no_access" }
              : p,
          ),
        );
      if (item.code === AGENT_CODE) {
        // Marcarse a uno mismo deja al store sin quien lo revierta si es su único admin: no se ofrece.
        const esUnoMismo =
          identity?.id != null && Number(identity.id) === Number(record?.userId || id);
        const sw = (
          <Switch
            checked={item.value === "admin"}
            disabled={esUnoMismo || soloLectura}
            onChange={(checked) =>
              // Prender o apagar el asistente le cambia a ese usuario cómo entra (D63): se confirma.
              checked
                ? confirmarAsistente(() => poner(true))
                : confirmarQuitarAsistente(() => poner(false))
            }
          />
        );
        return esUnoMismo ? (
          <Tooltip title="No podés marcarte a vos mismo como asistente IA">{sw}</Tooltip>
        ) : (
          sw
        );
      }
      return (
        <Switch
          checked={item.value === "admin"}
          onChange={(checked) => poner(checked)}
        />
      );
    }

    const selected = groupKey ? groupOptions[groupKey] : null;

    // Un permiso individual —sin grupo— ofrece exactamente los niveles que declara la API para
    // ese código, en su orden. `store_operators` trae tres (Admin, Lector, No; xcons-docs#537) y
    // los demás los cuatro de siempre, que coinciden con `PERMISSION_OPTIONS`.
    const declaradas =
      !groupKey && !isConfig && Array.isArray(item.options) && item.options.length > 0
        ? item.options.map((value: string) => ({
            label: (NIVEL as Record<string, string>)[value] ?? value,
            value,
          }))
        : null;

    let options =
      declaradas ??
      (selected === "admin"
        ? ADMIN_OPTIONS
        : selected === "editor"
          ? EDITOR_OPTIONS
          : selected === "viewer"
            ? VIEWER_OPTIONS
            : selected === "by_relation"
              ? PARTICIPATION_OPTIONS
              : PERMISSION_OPTIONS);

    if (item.code === "automation") {
      options = [
        { label: t("storeoperators.options.yes"), value: "admin" },
        { label: t("storeoperators.options.no"), value: "by_relation" },
      ];
    }
    if (item.code === "categorias") options = opcionesDeCategorias(item);

    return (
      <Select
        options={options}
        value={item.value}
        onChange={(value) => {
          if (isConfig) {
            setConfigs((prev: any) =>
              prev.map((c: any) =>
                c.code === item.code ? { ...c, value } : c,
              ),
            );
          } else {
            setPermissions((prev: any) =>
              prev.map((p: any) =>
                p.code === item.code ? { ...p, value } : p,
              ),
            );
          }
        }}
        style={{ minWidth: 180 }}
      />
    );
  };

  const updateGroupValues = (
    permissionCodes: string[],
    configCodes: string[],
    value: string,
    mainPermissionCode?: string,
  ) => {
    const isByRelation = value === "by_relation";
    const appliedChildValue = isByRelation ? "no_access" : value;

    setPermissions((prev) =>
      prev.map((p) => {
        if (p.code === mainPermissionCode) {
          return { ...p, value };
        }
        if (permissionCodes.includes(p.code)) {
          return { ...p, value: appliedChildValue };
        }
        return p;
      }),
    );

    // These configs are not permission-level enums, so the bulk group value
    // must not be written into them.
    const skipBulkConfigCodes = [
      "quote_max_manual_discount",
      "quote_access_margins",
      "quote_commission_discount_level",
    ];
    setConfigs((prev) =>
      prev.map((c) =>
        configCodes.includes(c.code) && !skipBulkConfigCodes.includes(c.code)
          ? { ...c, value: appliedChildValue }
          : c,
      ),
    );
  };

  const handleConfigChange = (code: string, value: string | number | null) => {
    // Upsert: existing operators may not have a row for the newer configs
    // (quote_access_margins / quote_commission_discount_level) yet.
    setConfigs((prevConfigs: any) => {
      const exists = prevConfigs.some((c: any) => c.code === code);
      if (exists) {
        return prevConfigs.map((config: any) =>
          config.code === code ? { ...config, value } : config,
        );
      }
      return [...prevConfigs, { code, value }];
    });
  };

  // ---------------------------------------------------------------------------
  // Orders group: switch-based sub-permission UX (see Permissions Spec).
  //
  // The two sub-permissions (`order_payment`, `order_shipment`) are rendered as
  // boolean switches whose state derives from the main `order` permission:
  //
  //   - main=admin            → both switches ON, disabled (forced to `admin`).
  //   - main=editor           → switches editable independently. ON = `editor`,
  //                              OFF = `no_access`.
  //   - main=viewer           → both switches OFF, disabled (forced to
  //                              `no_access`). A Viewer doesn't operate, so
  //                              granting sub-section access has no effect.
  //   - main=by_relation      → switches hidden. Access is decided per-record
  //                              via the order's lateral panel; nothing to
  //                              configure globally here.
  //
  // The runtime state mirrors these rules through `normalizeOrdersForSave`
  // before persisting — values typed during stale-main states (e.g., user
  // toggled while main was Editor, then switched to Viewer) are normalized
  // server-bound so DB never holds an incoherent combo.
  // ---------------------------------------------------------------------------
  const isOrdersSubOn = (value: string | undefined): boolean =>
    !!value && value !== "no_access";

  const isOrdersSwitchDisabled = (mainValue: string | undefined): boolean =>
    mainValue === "admin" || mainValue === "viewer";

  const areOrdersSwitchesHidden = (mainValue: string | undefined): boolean =>
    mainValue === "by_relation";

  const handleOrderSubToggle = (
    code: "order_payment" | "order_shipment",
    mainValue: string | undefined,
    checked: boolean,
  ) => {
    // ON inherits the main's level (admin/editor); OFF persists as no_access.
    // Falling back to `editor` keeps the switch sensible if the main has not
    // resolved yet (e.g., race during initial load).
    const onValue =
      mainValue === "admin"
        ? "admin"
        : mainValue === "editor"
          ? "editor"
          : "editor";
    const next = checked ? onValue : "no_access";
    setPermissions((prev) =>
      prev.map((p) => (p.code === code ? { ...p, value: next } : p)),
    );
  };

  const normalizeOrdersForSave = (
    list: PermissionItem[],
  ): PermissionItem[] => {
    const main = list.find((p) => p.code === "order")?.value;
    if (!main) return list;
    return list.map((p) => {
      if (p.code !== "order_payment" && p.code !== "order_shipment") return p;
      if (main === "admin") return { ...p, value: "admin" };
      if (main === "viewer" || main === "by_relation")
        return { ...p, value: "no_access" };
      if (main === "editor") {
        // ON → editor, OFF → no_access. Stale `admin`/`viewer` values get
        // coerced into `editor` so the switch's apparent ON state matches DB.
        return { ...p, value: p.value === "no_access" ? "no_access" : "editor" };
      }
      return p;
    });
  };

  const handleRemoveOrderParticipations = async () => {
    const userID = record?.userId || id;
    if (!userID || !storeIdentifier) return;
    try {
      const { data } = await axiosInstance.delete(
        `${API_URL_PERMISSIONS}/participation/operator/${userID}/store/${storeIdentifier}?permissionCode=order`,
      );
      const deleted = data?.deleted ?? 0;
      message.success(
        t(
          "storeoperator.message.orderParticipations.removed",
          "Participaciones eliminadas",
        ) + ` (${deleted})`,
      );
    } catch (error: any) {
      const apiMsg =
        error?.response?.data?.message || error?.response?.data?.error;
      message.error(
        Array.isArray(apiMsg)
          ? apiMsg.join(", ")
          : apiMsg ||
              t(
                "storeoperator.message.orderParticipations.removeError",
                "Error al remover participaciones",
              ),
      );
    }
  };

  const confirmRemoveOrderParticipations = () => {
    Modal.confirm({
      title: t(
        "storeoperator.message.title.removeOrderParticipations",
        "¿Remover todas las participaciones del operador en pedidos?",
      ),
      content: t(
        "storeoperator.message.title.removeOrderParticipations.two",
        "Esta acción elimina solamente las participaciones existentes — el nivel de permiso configurado se mantiene. El operador podrá volver a auto-incluirse al editar nuevos pedidos.",
      ),
      onOk: () => {
        handleRemoveOrderParticipations();
      },
      width: 540,
      okText: t(
        "storeoperator.message.label.confirmRemoveOrderParticipations",
        "Remover participaciones",
      ),
      okType: "danger",
      okButtonProps: { type: "primary" },
      icon: <ExclamationCircleOutlined />,
      cancelButtonProps: { icon: <ArrowLeftOutlined /> },
      cancelText: t("buttons.back"),
      onCancel() {},
    });
  };

  const confirmarAsistente = (onOk: () => void) => {
    Modal.confirm({
      title: t(
        "permissions.store.agent.confirm.title",
        "¿Marcar como asistente IA?",
      ),
      content: (
        <Flex vertical gap={8}>
          <Text>
            {t(
              "permissions.store.agent.confirm.body",
              "Este usuario pasa a ser un asistente IA del store: lo usa el pod del asistente, no una persona.",
            )}
          </Text>
          <Text strong>
            {t(
              "permissions.store.agent.confirm.login",
              "Al guardar se le cierran las sesiones y deja de poder entrar al panel. Sólo le quedan Presupuestos y Contactos, y no puede tener permisos en otro store.",
            )}
          </Text>
          <Text type="secondary">
            {t(
              "permissions.store.agent.confirm.persona",
              "Si es una persona, no lo marques: no va a poder volver a entrar.",
            )}
          </Text>
        </Flex>
      ),
      okText: t("permissions.store.agent.confirm.ok", "Marcar como asistente"),
      cancelText: t("buttons.cancel"),
      icon: <ExclamationCircleOutlined />,
      onOk,
    });
  };

  const confirmarQuitarAsistente = (onOk: () => void) => {
    Modal.confirm({
      title: t(
        "permissions.store.agent.off.title",
        "¿Quitarle el asistente IA?",
      ),
      content: t(
        "permissions.store.agent.off.body",
        "Al guardar se le cierran las sesiones: el pod del asistente deja de atender con este usuario hasta que se lo vuelva a marcar.",
      ),
      okText: t("permissions.store.agent.off.ok", "Quitar asistente"),
      okButtonProps: { danger: true },
      cancelText: t("buttons.cancel"),
      icon: <ExclamationCircleOutlined />,
      onOk,
    });
  };

  const PERMISSION_GROUPS = [
    {
      key: "contacts",
      icon: <ContactsOutlined />,
      title: t("menu.label.contacts"),
      mainPermissionCode: "contact",
      subtitle: t("label.instruction.permissionscontact"),
      permissionCodes: [],
      configCodes: ["contact_conversation", "contact_quote"],
    },
    {
      key: "contacts_groups",
      icon: <TeamOutlined />,
      title: t("label.contactsgroups"),
      subtitle: t("label.permissionscontact.group"),
      permissionCodes: ["contact_group"],
      configCodes: [],
      noButton: true,
    },
    {
      key: "quotes",
      icon: <PresupuestoIcon />,
      title: t("menu.label.quotes"),
      mainPermissionCode: "quote",
      subtitle: t("label.instruction.permissionsquotes"),
      permissionCodes: [],
      configCodes: [
        "quote_participation",
        "quote_max_manual_discount",
        "quote_access_margins",
        "quote_commission_discount_level",
      ],
    },
    {
      key: "orders",
      icon: <BoxCloseIcon />,
      title: t("menu.label.orders"),
      mainPermissionCode: "order",
      subtitle: t("label.instruction.orders"),
      permissionCodes: ["order_payment", "order_shipment"],
      configCodes: [],
    },
    {
      key: "linked_seller",
      icon: <SellerIcon />,
      title: "Linked seller",
      permissionCodes: ["store_linked_seller"],
      configCodes: [],
      individually: true,
    },
    {
      key: "automation",
      icon: <Robot2Line />,
      title: t("groups.table.automations"),
      permissionCodes: ["automation"],
      configCodes: [],
      individually: true,
    },
    {
      // Operadores del Store (xcons-docs#537). `store_permission` ya no se ofrece: la API lo
      // resuelve siempre como Admin para quien pertenece al store y no lo devuelve acá.
      key: "store",
      icon: <KeyRound />,
      title: "Store",
      permissionCodes: ["store_operators"],
      configCodes: [],
      individually: true,
    },
    {
      /**
       * Categorías de productos: gobierna el árbol de categorías del store, así que va con la
       * administración del Store y no con las ventas (xcons/xcons-docs#518). Es lo único del
       * Catálogo que se da por store (R5): el acceso al Catálogo, la auto-inclusión al editar
       * y las participaciones se manejan desde el listado del Catálogo, no acá. Sus niveles
       * los declara el backend según el store (xcons/xcons-docs#439).
       *
       * El panel sigue mandando `catalog` y `product_participation` con lo que leyó, sin
       * filas: el guardado fusiona, así que esconderlos no los cambia.
       */
      key: "catalog",
      icon: <AppstoreOutlined />,
      title: t("label.permissions.productcategories"),
      permissionCodes: ["categorias"],
      configCodes: [],
      individually: true,
    },
    {
      // Asistente IA (xcons-docs#561, D62): el switch que marca al usuario como asistente del
      // store. Su configuración —proveedor, modelo, conocimiento— está en Stores → Asistente IA.
      key: "agent",
      icon: <RobotOutlined />,
      title: t("permissions.store.agent", "Asistente IA"),
      permissionCodes: [AGENT_CODE],
      configCodes: [],
      individually: true,
    },
    {
      // Apps → Optimizer del store (xcons-docs#524): si las fichas ofrecen el optimizador.
      key: "store_optimizer_config",
      icon: <ScissorOutlined />,
      title: t("permissions.store.optimizer", "Optimizer"),
      permissionCodes: ["store_optimizer_config"],
      configCodes: [],
      individually: true,
    },
  ];

  const individualPermissions = PERMISSION_GROUPS.filter((g) => g.individually);
  const groupedPermissions = PERMISSION_GROUPS.filter((g) => !g.individually);

  const LABELS_MAP: Record<string, React.ReactNode> = {
    contact_conversation: (
      <Text>
        <Trans
          i18nKey="label.permissionscontact.conversation"
          components={{ bold: <Text strong /> }}
        />
      </Text>
    ),
    contact_quote: <Text>{t("label.permissionscontact.quote")}</Text>,
    contact_group: (
      <Flex vertical>
        <Text strong>
          <TeamOutlined /> {t("label.contactsgroups")}
        </Text>
        <Flex>
          <Text style={{ fontSize: "12px", marginLeft: 16 }}>
            {t("label.permissionscontact.group")}
          </Text>
        </Flex>
      </Flex>
    ),
    quote_participation: <Text>{t("label.permissionsquotes")}</Text>,
    quote_max_manual_discount: <Text>{t("label.maximumdiscount")}</Text>,
    quote_access_margins: <Text>{t("label.accessmargins")}</Text>,
    quote_commission_discount_level: (
      <Text>{t("label.commissionlevel")}</Text>
    ),

    order_payment: <Text>{t("label.permissionspaymenttransactions")}</Text>,
    order_shipment: <Text>{t("Label.deliveries")}</Text>,
    store_operators: <Text strong>{t("pages.storeoperators.title")}</Text>,
    store_optimizer_config: (
      <Text strong>{t("permissions.store.optimizer", "Optimizer")}</Text>
    ),
    store_linked_seller: <Text strong>{t("pages.linked sellers.title")}</Text>,
    agent: (
      <Flex vertical>
        <Text strong>{t("permissions.store.agent", "Asistente IA")}</Text>
        <Text type="secondary" style={{ fontSize: "12px" }}>
          {t(
            "permissions.store.agent.hint",
            "Usuario asistente de este store. Se configura en Stores → Asistente IA.",
          )}
        </Text>
      </Flex>
    ),
    automation: <Text strong>{t("groups.table.automations")}</Text>,

    catalog: <Text>{t("label.permissions.catalog")}</Text>,
    categorias: <Text strong>{t("label.permissions.productcategories")}</Text>,
    product_participation: (
      <Text>{t("label.permissions.productparticipation")}</Text>
    ),
  };

  const getPermissionLabel = (code: string) =>
    LABELS_MAP[code] ?? `Permission: ${code}`;

  const getConfigLabel = (code: string) =>
    LABELS_MAP[code] ?? `Config: ${code}`;

  const handleRemoveGroup = (
    permissionCodes: string[],
    configCodes: string[],
  ) => {
    updateGroupValues(permissionCodes, configCodes, "no_access");

    if (configCodes.includes("quote_max_manual_discount")) {
      handleConfigChange("quote_max_manual_discount", 0);
    }
    if (configCodes.includes("quote_access_margins")) {
      // 028 — el config es la unidad del margen: "sin acceso" es 'none', no 'false'.
      handleConfigChange("quote_access_margins", "none");
    }
    if (configCodes.includes("quote_commission_discount_level")) {
      handleConfigChange("quote_commission_discount_level", "no_access");
    }
  };

  const handleSavePermissions = async () => {
    setStructureLoading(true);
    const userID = record?.userId || id;
    try {
      // Normalize orders sub-permissions before persisting so the value sent
      // to the backend always matches the rules the UI was rendering. Without
      // this, a stale combo (e.g., switch toggled while main was Editor, then
      // main switched to Viewer without saving in between) could be persisted.
      const normalizedPermissions = normalizeOrdersForSave(permissions);

      const payload = {
        type: typeIdentifier,
        identifier: storeIdentifier,
        userId: userID,
        isActive: isUserActive,
        permissions: normalizedPermissions.map((p) => ({
          code: p.code,
          value: p.value,
        })),
        configs: configs.map((c) => ({
          code: c.code,
          value: c.value,
        })),
      };

      await axiosInstance.post(`${API_URL_PERMISSIONS}`, payload);

      // Los permisos de canal no son parte de este payload: viven en otra base, en el
      // conversation-service. Se guardan con el mismo botón para que el operador no tenga que
      // acordarse de dos "Save" distintos, pero si fallan no se pierde lo que sí se guardó.
      try {
        await channelPermsRef.current?.persist();
      } catch (e: any) {
        message.warning(
          `Los permisos se guardaron, pero no los de canales: ${
            e?.response?.data?.message ?? e?.message ?? e
          }`,
        );
      }

      message.success("Permissions saved successfully");
    } catch (error: any) {
      console.error("Error al guardar permisos:", error);
      // Un 400 de la API (por ejemplo, un asistente con permisos fuera del store) se dice: sin
      // esto el guardado fallaba en silencio.
      const data = error?.response?.data;
      const apiMsg = data?.message ?? data?.error;
      message.error(
        data?.code === "AGENT_NOT_DEDICATED"
          ? AGENT_NOT_DEDICATED
          : Array.isArray(apiMsg)
            ? apiMsg.join(", ")
            : apiMsg || error?.message || "No se pudieron guardar los permisos",
      );
    } finally {
      setStructureLoading(false);
    }
  };

  const handleDelete = async () => {
    setStructureLoading(true);
    const userID = record?.userId || id;
    try {
      await axiosInstance.delete(
        `${API_URL_PERMISSIONS}/${typeIdentifier}/${storeIdentifier}/users/${userID}`,
      );

      message.success(t("operator.removed.successfully"));
      handleBack();
    } catch (error) {
      console.error("Error al obtener permisos:", error);
    } finally {
      setStructureLoading(false);
    }
  };

  const warningDelete = () => {
    Modal.confirm({
      title: t("storeoperator.message.title.removeoperator"),
      content: t("storeoperator.message.title.removeoperator.two"),
      onOk: () => {
        handleDelete();
      },
      width: 490,
      okText: t("storeoperator.message.label.confirmremove"),
      okType: "danger",
      okButtonProps: {
        type: "primary",
      },
      icon: <ExclamationCircleOutlined />,
      cancelButtonProps: {
        icon: <ArrowLeftOutlined />,
      },
      cancelText: t("buttons.back"),
      onCancel() { },
    });
  };

  const handleRemoveRelations = async () => {
    const userID = record?.userId || id;
    if (!userID || !storeIdentifier) return;
    try {
      const decodedStoreCode = decodeURIComponent(storeIdentifier);
      const { data } = await axiosInstance.delete(
        `${API_URL_CONTACTS}/relations/operator/${userID}/store/${decodedStoreCode}`,
      );
      const deleted = data?.deleted ?? 0;
      message.success(
        t("storeoperator.message.relations.removed", "Relaciones eliminadas") +
          ` (${deleted})`,
      );
    } catch (error: any) {
      const apiMsg =
        error?.response?.data?.message || error?.response?.data?.error;
      message.error(
        Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg ||
          t(
            "storeoperator.message.relations.removeError",
            "Error al remover relaciones",
          ),
      );
    }
  };

  const confirmRemoveRelations = () => {
    Modal.confirm({
      title: t(
        "storeoperator.message.title.removerelations",
        "¿Remover todas las relaciones?",
      ),
      content: t(
        "storeoperator.message.title.removerelations.two",
        "El operador dejará de auto-asignarse a nuevas conversaciones y presupuestos de sus clientes actuales. Las participaciones en registros históricos se preservan. Esta acción no se puede deshacer.",
      ),
      onOk: () => {
        handleRemoveRelations();
      },
      width: 520,
      okText: t(
        "storeoperator.message.label.confirmremoverelations",
        "Remover relaciones",
      ),
      okType: "danger",
      okButtonProps: { type: "primary" },
      icon: <ExclamationCircleOutlined />,
      cancelButtonProps: { icon: <ArrowLeftOutlined /> },
      cancelText: t("buttons.back"),
      onCancel() {},
    });
  };

  return (
    <Spin spinning={structureLoading}>
      <ConfigProvider componentDisabled={soloLectura}>
      <Flex vertical>
        {esLector && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t(
              "storeoperators.readonly.title",
              "Sólo lectura",
            )}
            description={t(
              "storeoperators.readonly.body",
              "Tu nivel en Operadores del Store de esta tienda es Lector: podés ver los permisos de cada operador, pero no cambiarlos.",
            )}
          />
        )}
        <Flex align="center" justify="space-between">
          <Flex align="center">
            <ListButton
              onClick={handleBack}
              hideText
              disabled={false}
              icon={<ArrowLeftOutlined />}
            />
            <Title style={{ marginBottom: 0, marginLeft: "10px" }} level={3}>
              {`${t("permissionsOf")} ${record?.firstname || userData?.firstname
                } ${record?.lastname || userData?.lastname}`}
            </Title>
          </Flex>

          <Flex align="center" gap={6}>
            <Text style={{ color: token.colorTextSecondary }}>FAQ</Text>
            <ReadOutlined style={{ color: token.colorTextSecondary }} />
          </Flex>
        </Flex>

        <Card
          style={{
            margin: "16px 0",
            border: `1px solid ${token.colorBorder}`,
            borderRadius: "8px",
          }}
          styles={{ body: { padding: "8px 12px" } }}
        >
          <Flex
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
          >
            <Flex vertical gap={8}>
              <Flex align="center" gap={8}>
                <MailOutlined />
                <Text>{record?.email || userData?.email || "-"}</Text>
              </Flex>
              <Flex align="center" gap={8}>
                <WhatsAppOutlined />
                <Text>{record?.whatsapp || userData?.whatsapp || "-"}</Text>
              </Flex>
              <Text style={{ fontSize: "12px" }}>
                {t("userPermissions.editProfileInfo")}
              </Text>
            </Flex>
            <Flex vertical gap={25} align="end">
              <Flex align="center">
                <Text style={{ marginRight: "8px" }}>
                  {isUserActive
                    ? t("filter.products.labelStatusActive")
                    : t("filter.products.labelStatusDesactive")}
                </Text>
                <Switch
                  checked={isUserActive}
                  onChange={setIsUserActive}
                  className={isUserActive ? "active-switch" : ""}
                />
              </Flex>
              {!soloLectura && (
                <Flex gap="8px">
                  <LateralModal
                    key="copy-permissions-modal"
                    buttonName={t("copyPermissions")}
                    title={t("copyPermissions")}
                    buttonType="default"
                    buttonShape="default"
                    maskClosable={false}
                    iconSvg={<CopyOutlined />}
                  >
                    <StoresUserPermissionsCopy
                      t={t}
                      usersData={record || userData}
                      sellerCode={storeIdentifier}
                      permissions={permissions}
                      configs={configs}
                    />
                  </LateralModal>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={confirmRemoveRelations}
                  >
                    {t(
                      "storeoperator.button.removerelations",
                      "Remover relaciones",
                    )}
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Card>

        <Flex vertical gap={24}>
          <Collapse defaultActiveKey={["permissions"]}>
            <Panel
              className="permissions-collapse"
              key="permissions"
              header={<Text strong>{t("menu.label.salesManagement")}</Text>}
            >
              {groupedPermissions.map((group, index) => (
                <>
                  {/* Conversations va entre Contacts y Quotes (Figma). Se renderiza acá, atado al
                      grupo anterior, porque no es parte de PERMISSION_GROUPS: su estado vive en el
                      conversation-service, no en el modelo de permisos del main API. */}
                  {group.key === "quotes" && (
                    <ChannelAccessPermissions
                      ref={channelPermsRef}
                      storeCode={decodeURIComponent(storeIdentifier)}
                      subject={{ kind: "operator", userId: Number(record?.userId || id) }}
                    />
                  )}
                  <Card
                    key={`${group.key}-${index}`}
                    className={`permissions-card-section ${group.key === "contacts_groups"
                        ? "contacts-groups-card"
                        : ""
                      }`}
                    title={
                      !group.noButton &&
                      (group.permissionCodes.length > 1 ||
                        group.configCodes.length > 1) && (
                        <Flex justify="space-between" align="center">
                          <Flex vertical>
                            <Text
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              {group.icon} {group.title}
                            </Text>
                            <Text style={{ fontSize: "12px", marginLeft: 19 }}>
                              {group.subtitle}
                            </Text>
                          </Flex>

                          <Select
                            options={ALL_PERMISSION_OPTIONS}
                            placeholder="Select"
                            value={groupOptions[group.key]}
                            onChange={(value) => {
                              updateGroupValues(
                                group.permissionCodes,
                                group.configCodes,
                                value,
                                group.mainPermissionCode,
                              );
                              setGroupOptions((prev: any) => ({
                                ...prev,
                                [group.key]: value,
                              }));
                            }}
                            style={{ minWidth: 180 }}
                          />
                        </Flex>
                      )
                    }
                  >
                    {/* Renderizar configs */}
                    {group.configCodes.map((code, index) => {
                      const configItem = getConfig(code);
                      const isDiscountConfig =
                        code === "quote_max_manual_discount";
                      const isMarginToggle = code === "quote_access_margins";
                      const isCommissionLevel =
                        code === "quote_commission_discount_level";
                      const discountValue = Number(configItem?.value ?? 0);

                      return (
                        <Flex
                          key={`${group.key}-${code}-${index}`}
                          justify="space-between"
                          align="center"
                          style={{
                            marginBottom: 16,
                          }}
                        >
                          <Text>{getConfigLabel(code)}</Text>

                          {isDiscountConfig ? (
                            <InputNumber
                              style={{
                                minWidth: 180,
                                textAlignLast: "center",
                              }}
                              min={0}
                              max={100}
                              value={discountValue}
                              placeholder="-"
                              onChange={(value) => {
                                handleConfigChange(code, value);
                              }}
                            />
                          ) : isMarginToggle ? (
                            // 028 — la UNIDAD del margen, no un toggle. El valor se
                            // normaliza al renderizar porque las filas legacy siguen
                            // en 'true'/'false' y nunca se migran: sin eso, un store
                            // que hoy ve márgenes abriría el panel en "No access" y,
                            // al guardar, se sacaría el permiso a sí mismo.
                            <Select
                              style={{ minWidth: 180 }}
                              value={normalizeMarginUnit(getConfig(code)?.value)}
                              options={MARGIN_UNIT_OPTIONS}
                              onChange={(value) =>
                                handleConfigChange(code, value)
                              }
                            />
                          ) : isCommissionLevel ? (
                            <Select
                              style={{ minWidth: 180 }}
                              value={getConfig(code)?.value ?? "no_access"}
                              options={[
                                {
                                  label: t("label.commission.editor"),
                                  value: "editor",
                                },
                                {
                                  label: t("label.commission.viewer"),
                                  value: "viewer",
                                },
                                {
                                  label: t("label.commission.none"),
                                  value: "no_access",
                                },
                              ]}
                              onChange={(value) =>
                                handleConfigChange(code, value)
                              }
                            />
                          ) : (
                            renderSelect(configItem, true, group.key)
                          )}
                        </Flex>
                      );
                    })}

                    {/* Renderizar permisos */}
                    {group.permissionCodes.map((code, index) => {
                      // Orders group: payment/shipment render as switches
                      // derived from the main `order` permission. See the
                      // Permissions Spec for the truth table.
                      const isOrdersSub =
                        group.key === "orders" &&
                        (code === "order_payment" ||
                          code === "order_shipment");
                      if (isOrdersSub) {
                        const mainValue = groupOptions["orders"];
                        if (areOrdersSwitchesHidden(mainValue)) return null;
                        const item = getPermission(code);
                        const disabled = isOrdersSwitchDisabled(mainValue);
                        // Derive visible ON state: disabled-by-admin always
                        // shows ON, disabled-by-viewer always OFF, otherwise
                        // it follows the stored value.
                        const checked =
                          mainValue === "admin"
                            ? true
                            : mainValue === "viewer"
                              ? false
                              : isOrdersSubOn(item?.value);
                        return (
                          <Flex
                            key={`${group.key}-${code}-${index}`}
                            justify="space-between"
                            align="center"
                            style={{ marginBottom: 16 }}
                          >
                            <Text>{getPermissionLabel(code)}</Text>
                            <Switch
                              checked={checked}
                              disabled={disabled}
                              onChange={(c) =>
                                handleOrderSubToggle(
                                  code as
                                    | "order_payment"
                                    | "order_shipment",
                                  mainValue,
                                  c,
                                )
                              }
                            />
                          </Flex>
                        );
                      }
                      const item = getPermission(code);
                      if (esBinario(item)) {
                        // SI/NO, no los cuatro niveles: el permiso sólo contesta "¿entra
                        // a la sección?". La granularidad fina del Catálogo la da la
                        // participación por producto.
                        return (
                          <Flex
                            key={`${group.key}-${code}-${index}`}
                            justify="space-between"
                            align="center"
                            style={{ marginBottom: 16 }}
                          >
                            <Text>{getPermissionLabel(code)}</Text>
                            <Switch
                              checked={item?.value === "admin"}
                              onChange={(checked) =>
                                setPermissions((prev) =>
                                  prev.map((p) =>
                                    p.code === code
                                      ? {
                                          ...p,
                                          value: checked
                                            ? "admin"
                                            : "no_access",
                                        }
                                      : p,
                                  ),
                                )
                              }
                            />
                          </Flex>
                        );
                      }
                      return (
                        <Flex
                          key={`${group.key}-${code}-${index}`}
                          justify="space-between"
                          align="center"
                          style={group.noButton ? {} : { marginBottom: 16 }}
                        >
                          <Text>{getPermissionLabel(code)}</Text>
                          {renderSelect(item, false, group.key)}
                        </Flex>
                      );
                    })}

                    {!group.noButton &&
                      (group.key === "orders" ? (
                        // Spec-specific action: clears existing participations
                        // (permission_participation rows) without touching the
                        // configured level. Future tracking can re-add them on
                        // new edits.
                        <Flex justify="space-between" align="center">
                          <Text>
                            {t(
                              "label.permissionsorders.removeparticipations",
                              "Remove all operator's existing participations from orders",
                            )}
                          </Text>
                          <Button
                            danger
                            style={{
                              marginTop: 16,
                              width: 180,
                              height: 32,
                              fontSize: 16,
                            }}
                            onClick={confirmRemoveOrderParticipations}
                          >
                            {t("buttons.remove")}
                          </Button>
                        </Flex>
                      ) : (
                        <Flex justify="space-between" align="center">
                          <Text>
                            {t("label.permissionscontact.removerelations")}{" "}
                            {group?.title.toLowerCase()}
                          </Text>
                          <Button
                            danger
                            style={{
                              marginTop: 16,
                              width: 180,
                              height: 32,
                              fontSize: 16,
                            }}
                            onClick={() =>
                              handleRemoveGroup(
                                group.permissionCodes,
                                group.configCodes,
                              )
                            }
                          >
                            {t("buttons.remove")}
                          </Button>
                        </Flex>
                      ))}
                  </Card>
                </>
              ))}
            </Panel>
          </Collapse>

          <Collapse defaultActiveKey={["store"]}>
            <Panel
              className="permissions-collapse"
              key="store"
              header={
                <Text strong>
                  <span style={{ textTransform: "capitalize" }}>
                    {storeName}
                  </span>{" "}
                  Store administration
                </Text>
              }
            >
              {individualPermissions.map((group, index) => (
                <Flex
                  key={`indiv-${group.key}-${index}`}
                  style={{ marginBottom: 16 }}
                >
                  {/* Permisos individuales */}
                  {group.permissionCodes.map((code, i) => (
                    <Card
                      key={`perm-${group.key}-${code}-${i}`}
                      className="permissions-card-indivituallity"
                    >
                      <Flex justify="space-between" align="center">
                        <Flex align="center" gap={8}>
                          {group.icon}
                          <Text>{getPermissionLabel(code)}</Text>
                        </Flex>
                        {renderSelect(getPermission(code))}
                      </Flex>
                    </Card>
                  ))}

                  {/* Configuraciones individuales */}
                  {group.configCodes.map((code, i) => (
                    <Card
                      key={`conf-${group.key}-${code}-${i}`}
                      className="permissions-card-indivituallity"
                    >
                      <Flex justify="space-between" align="center">
                        <Flex align="center" gap={8}>
                          {group.icon}
                          <Text>{getConfigLabel(code)}</Text>
                        </Flex>
                        {renderSelect(getConfig(code), true)}
                      </Flex>
                    </Card>
                  ))}
                </Flex>
              ))}
            </Panel>
          </Collapse>
        </Flex>
        <Flex justify="space-between" style={{ marginTop: 16 }}>
          {soloLectura ? (
            <span />
          ) : (
            <Button
              danger
              icon={<DeleteOutlined />}
              iconPosition="end"
              onClick={warningDelete}
            >
              {t("button.removeoperator")}
            </Button>
          )}
          <Flex gap={8}>
            <Button onClick={handleBack} disabled={false}>
              {t("buttons.cancel")}
            </Button>
            {!soloLectura && (
              <Button
                type="primary"
                icon={<CheckOutlined />} iconPosition="end"
                onClick={handleSavePermissions}
              >
                {t("button.save")}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
      </ConfigProvider>
    </Spin>
  );
};
